import React, { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import TabsComponent from 'reusableComponents/TabsComponent'
import useDetectTablet from 'hooks/useDetectTablet'
import { ReactComponent as UserIcon } from 'assets/svg/user.svg'
import { ReactComponent as GearIcon } from 'assets/svg/gearIcon.svg'
import { ReactComponent as CubeIcon } from 'assets/svg/cube.svg'
import MyProfile from 'components/MyProfile'
import MyPreferences from 'components/MyPreferences'
import MyShipments from 'components/MyShipments'
import SendOTPModal from 'components/SendOTPModal'
import VerifyOTPModal from 'components/VerifyOTPModal'
import {
  selectUserProfile,
  selectUserPreferences,
  selectUserShipments,
} from 'store/recipient/recipientSelectors'
import {
  getUserPreferencesInfo,
  getUserProfileInfo,
  getUserShipments,
} from 'store/recipient/recipientSlice'
import {
  selectIsOtpVerificationRequired,
  selectPhoneNumForOtpVerification,
} from 'store/shipping/shippingSelectors'
import { useDispatch, useSelector } from 'react-redux'
import RecipientInfoSpinner from 'components/RecipientInfoContent/RecipientInfoSpinner'
import styled from 'styled-components'
import { breakpoints } from 'globalStyles'

const pywBackendURL = process.env.REACT_APP_PYW_BACKEND_API

export const TabsContainer = styled.div`
  width: 98%;
  @media ${breakpoints.desktop} {
    max-width: 1030x;
  }
  @media ${breakpoints.tablet} {
    width: 83%;
  }
`

const getTabsList = (t, userProfileInfo, userShipmentInfo, userPreferencesInfo, tabIconStyles) => [
  {
    tabKey: 'shipments',
    tabTitle: t(`header.my_shipments`),
    component: userShipmentInfo ? (
      <MyShipments userShipments={userShipmentInfo} />
    ) : (
      <RecipientInfoSpinner loadingText={t(`recipientInfo.loading_shipments`)} />
    ),
    tabIcon: CubeIcon,
    tabIconStyle: tabIconStyles.shipments,
    showDotIcon: false,
  },
  {
    tabKey: 'profile',
    tabTitle: t(`header.my_profile`),
    component: userProfileInfo ? (
      <MyProfile userProfileDetails={userProfileInfo.data} />
    ) : (
      <RecipientInfoSpinner loadingText={t(`recipientInfo.loading_profile`)} />
    ),
    tabIcon: UserIcon,
    tabIconStyle: tabIconStyles.profile,
    showDotIcon: false,
  },
  {
    tabKey: 'preferences',
    tabTitle: t(`header.my_preferences`),
    component: userPreferencesInfo ? (
      <MyPreferences userPreferenceDetails={userPreferencesInfo.data} />
    ) : (
      <RecipientInfoSpinner loadingText={t(`recipientInfo.loading_preferences`)} />
    ),
    tabIcon: GearIcon,
    tabIconStyle: tabIconStyles.preferences,
    showDotIcon: true,
  },
]

const RecipientInfoContent = () => {
  const prn = pywBackendURL
  const dispatch = useDispatch()
  const { lang } = useParams()
  const { i18n, t } = useTranslation()
  const isOtpVerificationRequired = useSelector(selectIsOtpVerificationRequired)
  const { recipientPhoneNumber } = useSelector(selectPhoneNumForOtpVerification)
  const [openSendOtpCodeModal, setOpenSendOtpCodeModal] = useState(isOtpVerificationRequired)
  const [openVerificationCodeModal, setOpenVerificationCodeModal] = useState(false)
  const showVerificationCodeModal = useCallback(() => {
    setOpenVerificationCodeModal(true)
  })

  useEffect(() => {
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang)
    }
    dispatch(getUserProfileInfo(prn))
    dispatch(getUserPreferencesInfo(prn))
    dispatch(getUserShipments(0))
  }, [dispatch, prn, lang, i18n])

  const userProfileInfo = useSelector(selectUserProfile)
  const userPreferencesInfo = useSelector(selectUserPreferences)
  const userShipmentInfo = useSelector(selectUserShipments)

  const defaultActiveTab = 'shipments'
  let arePreferencesSet = false
  if (
    userPreferencesInfo &&
    (userPreferencesInfo.data?.releaseWithoutSignature ||
      userPreferencesInfo.data?.deliveryLocation)
  ) {
    arePreferencesSet = true
  }
  // Device detection
  const isTablet = useDetectTablet()

  // Define icon styles for each device type
  let shipmentsIconStyle
  let profileIconStyle
  let preferencesIconStyle
  const baseIconStyle = { verticalAlign: 'super' }
  if (isTablet) {
    shipmentsIconStyle = { ...baseIconStyle, width: '24px', height: '25px' }
    profileIconStyle = { ...baseIconStyle, width: '25px', height: '25px' }
    preferencesIconStyle = { ...baseIconStyle, width: '23px', height: '25px' }
  } else {
    shipmentsIconStyle = { ...baseIconStyle, width: '20.1px', height: '20.64px' }
    profileIconStyle = { ...baseIconStyle, width: '20px', height: '20px' }
    preferencesIconStyle = { ...baseIconStyle, width: '20px', height: '21px' }
  }

  const tabIconStyles = {
    shipments: shipmentsIconStyle,
    profile: profileIconStyle,
    preferences: preferencesIconStyle,
  }

  return (
    <TabsContainer>
      <TabsComponent
        tabItems={getTabsList(
          t,
          userProfileInfo,
          userShipmentInfo,
          userPreferencesInfo,
          tabIconStyles
        )}
        defaultActiveTab={defaultActiveTab}
        hasToDoTasks={arePreferencesSet}
      />
      {isOtpVerificationRequired && (
        <SendOTPModal
          show={openSendOtpCodeModal}
          phoneNumber={recipientPhoneNumber}
          showVerificationCodeModal={showVerificationCodeModal}
          onClose={() => setOpenSendOtpCodeModal(false)}
        />
      )}
      {openVerificationCodeModal && (
        <VerifyOTPModal
          show={openVerificationCodeModal}
          onHide={(status) => {
            setOpenVerificationCodeModal(false)
            if (status === 'success') {
              setOpenSendOtpCodeModal(false)
            }
          }}
          phoneNumber={recipientPhoneNumber}
          showModalFor="verifyOtpEvery6Months"
        />
      )}
    </TabsContainer>
  )
}

export default RecipientInfoContent
